// Unit search utilities for standardized wing-unit searching

export interface UnitSearchCriteria {
  wing?: string;
  unitNo?: string;
  searchTerm: string;
}

/**
 * Parse unit search term into wing and unitNo components
 * @param searchTerm - User input like "B-101", "101", "b101", etc.
 * @returns Object with wing and unitNo for database filtering
 */
export const parseUnitSearch = (searchTerm: string): UnitSearchCriteria => {
  if (!searchTerm) {
    return { searchTerm };
  }

  const cleanTerm = searchTerm.trim().toUpperCase();
  
  // Pattern 1: "B-101" or "B101" (wing + unit)
  const wingUnitMatch = cleanTerm.match(/^([A-Z]+)[-]?(\d+)$/);
  if (wingUnitMatch) {
    return {
      wing: wingUnitMatch[1],
      unitNo: wingUnitMatch[2],
      searchTerm
    };
  }
  
  // Pattern 2: "101" (unit only)
  const unitOnlyMatch = cleanTerm.match(/^(\d+)$/);
  if (unitOnlyMatch) {
    return {
      unitNo: unitOnlyMatch[1],
      searchTerm
    };
  }
  
  // Pattern 3: Anything else (partial search)
  return {
    searchTerm
  };
};

/**
 * Filter units based on standardized search criteria
 * @param units - Array of units to filter
 * @param searchTerm - User search input
 * @returns Filtered units array
 */
export const filterUnitsBySearch = <T extends { wing?: string | null; unitNo: string }>(
  units: T[], 
  searchTerm: string
): T[] => {
  if (!searchTerm) {
    return units;
  }

  const criteria = parseUnitSearch(searchTerm);
  
  return units.filter(unit => {
    // Exact wing-unit match (e.g., "C-301")
    if (criteria.wing && criteria.unitNo) {
      const wingMatch = unit.wing?.toUpperCase() === criteria.wing;
      const unitNoMatch = unit.unitNo === criteria.unitNo;
      return wingMatch && unitNoMatch;
    }
    
    // Unit number only match (e.g., "301") - must have NO wing or empty wing
    if (criteria.unitNo && !criteria.wing) {
      const unitNoMatch = unit.unitNo === criteria.unitNo;
      const noWing = !unit.wing || unit.wing.trim() === '';
      return unitNoMatch && noWing;
    }
    
    // No match if criteria doesn't match patterns above
    return false;
  });
};

/**
 * Create standardized unit display format
 * @param wing - Unit wing (optional)
 * @param unitNo - Unit number
 * @returns Formatted string like "B-101" or "101"
 */
export const formatUnitDisplay = (wing?: string | null, unitNo?: string): string => {
  if (!unitNo) return '';
  
  if (wing) {
    return `${wing}-${unitNo}`;
  }
  
  return unitNo;
};

/**
 * Create database query conditions for unit search
 * @param searchTerm - User search input
 * @returns Prisma where conditions
 */
export const createUnitSearchConditions = (searchTerm: string) => {
  if (!searchTerm) {
    return {};
  }

  const criteria = parseUnitSearch(searchTerm);
  
  // Exact wing-unit match (e.g., "C-301")
  if (criteria.wing && criteria.unitNo) {
    return {
      AND: [
        { wing: { equals: criteria.wing, mode: 'insensitive' } },
        { unitNo: { equals: criteria.unitNo } }
      ]
    };
  }
  
  // Unit number only match (e.g., "301") - must have NO wing or empty wing
  if (criteria.unitNo && !criteria.wing) {
    return {
      AND: [
        { unitNo: { equals: criteria.unitNo } },
        { 
          OR: [
            { wing: null },
            { wing: '' }
          ]
        }
      ]
    };
  }
  
  // No match if criteria doesn't match patterns above - return impossible condition
  return {
    id: -1 // Will match nothing
  };
};